//회원가입 입력 폼 (입학년도, 생년월일 입력 포함)
import React, { useState } from "react";
import FormInput from "../common/FormInput";
import { supabase } from "../../lib/supabaseClient";
import { useNavigate } from "react-router-dom";
import "./SignupForm.css";

function SignupForm() {
  const [formData, setFormData] = useState({
    userId: "",
    password: "",
    email: "",
    name: "",
    studentId: "",
    department: "",
    birthYear: "",
    birthMonth: "",
    birthDay: "",
    admissionYear: "",
    grade: "",
    gender: "",
    phone: "",
  });
  const navigate = useNavigate();
  const [error, setError] = useState(null); // 에러 상태
  const [loading, setLoading] = useState(false); // 로딩 상태
  const [isIdChecked, setIsIdChecked] = useState(false); // 아이디 중복 체크 상태

  function handleChange (event){
    const { name, value } = event.target;
    // 아이디 입력값이 변경되면 중복 체크 상태를 리셋합니다.
    if (name === "userId") {
      setIsIdChecked(false);
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 아이디 중복 체크: 회원가입 전 users 테이블에서 동일한 login_id 존재 여부를 확인
  // isIdChecked 상태가 true일 때만 handleSubmit에서 회원가입이 진행됨
  const handleIdCheck = async () => {
    const { userId } = formData;
    if (!userId.trim()) {
      alert("아이디를 입력해주세요.");
      return;
    }

    try {
      const { data, error } = await supabase
        .from("users")
        .select("login_id")
        .eq("login_id", userId);  // 입력한 아이디와 정확히 일치하는 row 조회

      if (error) throw error;

      if (data && data.length > 0) {
        // 조회 결과가 1건 이상이면 이미 사용 중인 아이디
        alert("이미 사용 중인 아이디입니다.");
        setIsIdChecked(false);
      } else {
        alert("사용 가능한 아이디입니다.");
        setIsIdChecked(true);  // 중복 없음이 확인된 경우에만 가입 가능 상태로 변경
      }
    } catch (error) {
      console.error("아이디 중복 체크 에러:", error);
      alert(`오류가 발생했습니다: ${error.message}`);
    }
  };

  const handleSubmit = async (event) =>{
    event.preventDefault();

    if (!isIdChecked) {
      alert("아이디 중복 체크를 완료해주세요.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // [회원가입 2단계 처리]
      // 1단계: Supabase Auth에 인증 계정 생성 (이메일 + 비밀번호)
      // → auth.users 테이블에 UUID 기반의 인증 정보가 저장되고, 해당 UUID가 authData.user.id로 반환됨
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });

      if (authError) throw authError;

      if (!authData.user) {
        throw new Error("사용자 정보가 생성되지 않았습니다. 다시 시도해주세요.");
      }

      // 2단계: public.users 테이블에 추가 프로필 정보 저장
      // id를 auth UUID(authData.user.id)와 동일하게 설정해 두 테이블을 연결
      const birthDate = `${formData.birthYear}-${String(
        formData.birthMonth
      ).padStart(2, "0")}-${String(formData.birthDay).padStart(2, "0")}`;

      const profileData = {
        id: authData.user.id,  // auth UUID를 PK로 사용해 인증 계정과 프로필을 연결
        login_id: formData.userId,
        email: formData.email,
        name: formData.name,
        student_number: formData.studentId,
        department: formData.department,
        admission_year: formData.admissionYear,
        birth_date: birthDate,
        password: formData.password,
        grade: formData.grade,
        gender: formData.gender,
        phone: formData.phone,
        role: "user",  // 기본 역할은 일반 사용자, admin은 별도 부여
      };

      console.log("Inserting into public.users:", profileData);

      const { error: profileError } = await supabase
        .from("users")
        .insert([profileData]);

      if (profileError) {
        throw profileError;
      }

      alert("회원가입에 성공했습니다!");
      navigate("/login"); // 로그인 페이지로 이동

    } catch (error) {
      console.error("회원가입 에러:", error);
      const errorMessage = error.message;
      if (errorMessage.includes("User already registered")) {
        const specificError = "이미 가입된 이메일입니다. 다른 이메일을 사용해주세요.";
        setError(specificError);
        alert(specificError);
      } else {
        setError(errorMessage);
        alert(`회원가입 실패: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // 드롭다운 메뉴 옵션
  const currentYear = new Date().getFullYear();
  const birthYears = Array.from({ length: currentYear - 1950 + 1 }, (_, i) => currentYear - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const admissionYears = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => currentYear - i);

  return (
    <div className="signup-container">
      <h1 className="signup-title">회원가입</h1>

      {/* 에러 메시지 표시 영역 */}
      {error && <p className="error-message">{error}</p>}

      <form onSubmit={handleSubmit} className="signup-form">
        
        {/* 아이디 + 중복 체크 버튼 */}
        <div className="id-row">
          <div className="id-input-wrapper">
            <FormInput
              type="text"
              name="userId"
              placeholder="아이디..."
              value={formData.userId}
              onChange={handleChange}
            />
          </div>
          <button type="button" className="duplicate-check-btn" onClick={handleIdCheck}>
            중복 체크
          </button>
        </div>

        {/* 기본 입력창들 */}
        <FormInput type="password" name="password" placeholder="비밀번호..." value={formData.password} onChange={handleChange} />
        <FormInput type="email" name="email" placeholder="이메일..." value={formData.email} onChange={handleChange} />
        <FormInput type="text" name="name" placeholder="이름..." value={formData.name} onChange={handleChange} />

        {/* 생년월일 드롭다운 영역 */}
        <div className="select-row">
          <span className="select-label">생년월일:</span>
          <select name="birthYear" className="custom-select" value={formData.birthYear} onChange={handleChange}>
            <option value="">년도</option>
            {birthYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <select name="birthMonth" className="custom-select short-select" value={formData.birthMonth} onChange={handleChange}>
            <option value="">월</option>
            {months.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>
          <select name="birthDay" className="custom-select short-select" value={formData.birthDay} onChange={handleChange}>
            <option value="">일</option>
            {days.map((day) => (
              <option key={day} value={day}>
                {day}
              </option>
            ))}
          </select>
        </div>

        <FormInput type="text" name="studentId" placeholder="학번..." value={formData.studentId} onChange={handleChange} />
        <FormInput type="text" name="department" placeholder="학과..." value={formData.department} onChange={handleChange} />
        <FormInput type="tel" name="phone" placeholder="휴대폰 번호... (예: 010-1234-5678)" value={formData.phone} onChange={handleChange} />

        {/* 학년 드롭다운 */}
        <div className="select-row">
          <span className="select-label">학년:</span>
          <select name="grade" className="custom-select long-select" value={formData.grade} onChange={handleChange}>
            <option value="">학년 선택</option>
            {[1, 2, 3, 4].map((g) => (
              <option key={g} value={g}>{g}학년</option>
            ))}
          </select>
        </div>

        {/* 성별 드롭다운 */}
        <div className="select-row">
          <span className="select-label">성별:</span>
          <select name="gender" className="custom-select long-select" value={formData.gender} onChange={handleChange}>
            <option value="">성별 선택</option>
            <option value="남자">남자</option>
            <option value="여자">여자</option>
          </select>
        </div>

        {/* 입학년도 드롭다운 영역 */}
        <div className="select-row">
          <span className="select-label">입학년도:</span>
          <select name="admissionYear" className="custom-select long-select" value={formData.admissionYear} onChange={handleChange}>
            <option value="">년도 선택</option>
            {admissionYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        {/* 하단 회원 가입 버튼 */}
        <button type="submit" className="signup-submit-btn" disabled={loading}>
          {loading ? "가입 중..." : "회원 가입"}
        </button>
      </form>
    </div>
  );
}

export default SignupForm;